/**
 * Platform Detection Utilities
 * Detect user's operating system and provide platform-specific instructions
 */

export type Platform = "macos" | "windows" | "linux" | "unknown";

/**
 * Detect the user's platform based on user agent
 */
export function detectPlatform(): Platform {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const platform = window.navigator.platform?.toLowerCase() || "";

  if (platform.includes("mac") || userAgent.includes("mac")) {
    return "macos";
  }
  if (platform.includes("win") || userAgent.includes("win")) {
    return "windows";
  }
  if (platform.includes("linux") || userAgent.includes("linux")) {
    return "linux";
  }
  return "unknown";
}

/**
 * Get platform-specific display name
 */
export function getPlatformName(platform: Platform): string {
  const names: Record<Platform, string> = {
    macos: "macOS",
    windows: "Windows",
    linux: "Linux",
    unknown: "Unknown",
  };
  return names[platform];
}

/**
 * Get platform icon emoji
 */
export function getPlatformIcon(platform: Platform): string {
  const icons: Record<Platform, string> = {
    macos: "🍎",
    windows: "🪟",
    linux: "🐧",
    unknown: "💻",
  };
  return icons[platform];
}

/**
 * Get Python command for platform
 */
export function getPythonCommand(platform: Platform): string {
  if (platform === "windows") {
    return "python";
  }
  return "python3";
}

/**
 * Get virtual environment activation command
 * Note: Windows PowerShell uses $HOME, Unix uses ~
 */
export function getVenvActivateCommand(
  platform: Platform,
  venvPath?: string,
): string {
  if (platform === "windows") {
    // $HOME works in PowerShell (the default/recommended shell)
    const windowsPath = venvPath || "$HOME\\adk-workshop";
    return `${windowsPath}\\Scripts\\Activate`;
  }
  const unixPath = venvPath || "~/adk-workshop";
  return `source ${unixPath}/bin/activate`;
}

/**
 * Get the path for creating a virtual environment
 * Note: Windows PowerShell uses $HOME, Unix uses ~
 */
export function getVenvCreatePath(platform: Platform): string {
  if (platform === "windows") {
    // $HOME works in PowerShell (the default/recommended shell)
    return "$HOME\\adk-workshop";
  }
  return "~/adk-workshop";
}

/**
 * Get shell type based on platform
 */
export function getShellType(platform: Platform): string {
  if (platform === "windows") {
    return "PowerShell";
  }
  if (platform === "macos") {
    return "Terminal (zsh or bash)";
  }
  return "Terminal (bash)";
}

/**
 * Get instructions for opening the terminal on each platform
 */
export function getTerminalOpenInstructions(platform: Platform): {
  method: string;
  steps: string[];
  shortcut?: string;
  appName: string;
} {
  if (platform === "macos") {
    return {
      method: "Spotlight Search",
      steps: [
        "Press Cmd + Space to open Spotlight",
        'Type "Terminal"',
        "Press Enter to open the Terminal app",
      ],
      shortcut: "Cmd + Space",
      appName: "Terminal",
    };
  }
  if (platform === "windows") {
    return {
      method: "Start Menu or Search",
      steps: [
        "Press the Windows key or click Start",
        'Type "PowerShell"',
        'Click "Windows PowerShell" to open it',
      ],
      shortcut: "Win + X, then I",
      appName: "PowerShell",
    };
  }
  // Linux
  return {
    method: "Keyboard Shortcut or Application Menu",
    steps: [
      "Press Ctrl + Alt + T (works on most distributions)",
      'Or search for "Terminal" in your application menu',
    ],
    shortcut: "Ctrl + Alt + T",
    appName: "Terminal",
  };
}

/**
 * Get Python download URL for each platform
 */
export function getPythonDownloadUrl(platform: Platform): string {
  const baseUrl = "https://www.python.org/downloads/";
  if (platform === "macos") {
    return `${baseUrl}macos/`;
  }
  if (platform === "windows") {
    return `${baseUrl}windows/`;
  }
  // Linux users typically use package managers
  return baseUrl;
}

/**
 * Get Python installation tips for each platform
 */
export function getPythonInstallTips(platform: Platform): {
  tips: string[];
  warning?: string;
  packageManager?: string;
} {
  if (platform === "macos") {
    return {
      tips: [
        "Download the macOS installer from python.org",
        "Double-click the .pkg file to run the installer",
        "Follow the installation wizard (default settings are fine)",
        "Open a new Terminal window after installation",
      ],
      packageManager:
        "Alternatively, install via Homebrew: brew install python3",
    };
  }
  if (platform === "windows") {
    return {
      tips: [
        "Download the Windows installer from python.org",
        "Run the installer (.exe file)",
        'IMPORTANT: Check "Add Python to PATH" at the bottom of the first screen',
        'Click "Install Now" and wait for completion',
        "Open a new PowerShell window after installation",
      ],
      warning:
        'Make sure to check "Add Python to PATH" during installation, or Python commands won\'t work in your terminal!',
    };
  }
  // Linux
  return {
    tips: [
      "Python is often pre-installed on Linux",
      "If not, use your distribution's package manager",
    ],
    packageManager:
      "Ubuntu/Debian: sudo apt install python3\nFedora: sudo dnf install python3\nArch: sudo pacman -S python",
  };
}

/**
 * Get the command to check if Python is installed
 */
export function getPythonCheckCommand(platform: Platform): string {
  if (platform === "windows") {
    return "python --version";
  }
  return "python3 --version";
}

/**
 * Get common Python-related error messages and their solutions
 */
export function getPythonErrorSolutions(platform: Platform): Array<{
  error: string;
  solution: string;
}> {
  const commonErrors = [
    {
      error: "'python' is not recognized as an internal or external command",
      solution:
        platform === "windows"
          ? 'Python is not installed or not added to PATH. Reinstall Python and check "Add Python to PATH".'
          : "Try using 'python3' instead of 'python', or install Python from python.org.",
    },
    {
      error: "command not found: python",
      solution:
        "Python is not installed. Download and install it from python.org or use your package manager.",
    },
    {
      error: "Python 2.x.x",
      solution:
        "You have Python 2 installed, but we need Python 3.10 or higher. Install Python 3 from python.org.",
    },
  ];

  if (platform === "windows") {
    commonErrors.push({
      error: "Python was not found; run without arguments to install",
      solution:
        "Windows is redirecting to the Microsoft Store. Install Python from python.org instead for best compatibility.",
    });
  }

  return commonErrors;
}
